/**
 * Penjaga sesi: bila API menjawab 401 untuk permintaan yang membawa token login
 * tersimpan, token itu sudah tidak berlaku (kedaluwarsa sesuai JWT_EXPIRATION,
 * sandi diganti, atau akun dinonaktifkan -- server mencabut token lama). Tanpa
 * penjaga ini setiap layar hanya menampilkan galat "Unauthorized" tanpa jalan
 * keluar, dan petugas mengira server rusak. Dengan penjaga ini token dibuang dan
 * petugas diarahkan ke halaman login dengan penjelasan singkat.
 *
 * Aturan ketat supaya tidak pernah membuat putaran:
 *  - hanya permintaan ke alamat API Lumivix;
 *  - hanya yang membawa persis token login yang sedang tersimpan (login gagal,
 *    sesi tautan pasien/SIMRS, dan penampil tanpa token tidak ikut terhitung);
 *  - tidak pernah untuk /auth/login dan /auth/logout;
 *  - sekali berjalan, tidak diulang sampai halaman dimuat ulang.
 */
import API_CONFIG from '../config/apiConfig';
import { clearAuth, getToken } from './authService';

/** Kunci sessionStorage: dibaca halaman login untuk menjelaskan kenapa petugas dikeluarkan. */
export const SESSION_EXPIRED_FLAG = 'lumivix_session_expired';

const NEVER_FORCE_LOGOUT = ['/auth/login', '/auth/logout'];

function resolveUrl(raw: string, origin: string): URL | null {
  try {
    return new URL(raw, origin);
  } catch {
    return null;
  }
}

/** Apakah `rawUrl` menunjuk ke API Lumivix (sama-origin /lumi-api, atau alamat penuh saat pengembangan). */
export function isApiUrl(
  rawUrl: string,
  baseURL: string = API_CONFIG.baseURL,
  origin: string = window.location.origin
): boolean {
  const target = resolveUrl(rawUrl, origin);
  const base = resolveUrl(baseURL, origin);
  if (!target || !base) {
    return false;
  }
  if (target.origin !== base.origin) {
    return false;
  }
  const basePath = base.pathname.replace(/\/+$/, '');
  return target.pathname === basePath || target.pathname.startsWith(`${basePath}/`);
}

export interface ResponseFacts {
  url: string;
  status: number;
  /** Nilai header Authorization yang DIKIRIM pada permintaan itu (bukan yang tersimpan). */
  authorization: string | null;
  /** Token login yang sedang tersimpan di peramban. */
  storedToken: string | null;
}

/** Murni: haruskah respons ini mengakhiri sesi? */
export function isExpiredSession(
  facts: ResponseFacts,
  baseURL: string = API_CONFIG.baseURL,
  origin: string = window.location.origin
): boolean {
  if (facts.status !== 401 || !facts.storedToken) {
    return false;
  }
  if (facts.authorization !== `Bearer ${facts.storedToken}`) {
    return false;
  }
  if (!isApiUrl(facts.url, baseURL, origin)) {
    return false;
  }
  const path = resolveUrl(facts.url, origin)?.pathname ?? '';
  return !NEVER_FORCE_LOGOUT.some(suffix => path.endsWith(suffix));
}

/** Nilai satu header dari bentuk HeadersInit apa pun (Headers, larik pasangan, objek biasa). */
function headerValue(headers: HeadersInit | undefined, name: string): string | null {
  if (!headers) {
    return null;
  }
  const wanted = name.toLowerCase();
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return headers.get(name);
  }
  if (Array.isArray(headers)) {
    const found = headers.find(([key]) => String(key).toLowerCase() === wanted);
    return found ? String(found[1]) : null;
  }
  const record = headers as Record<string, string>;
  const key = Object.keys(record).find(k => k.toLowerCase() === wanted);
  return key ? String(record[key]) : null;
}

function readAuthorization(input: RequestInfo | URL, init?: RequestInit): string | null {
  try {
    if (init?.headers) {
      return headerValue(init.headers, 'Authorization');
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
      return input.headers.get('Authorization');
    }
  } catch {
    // Header tak terbaca: anggap tidak membawa token.
  }
  return null;
}

function readUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (typeof URL !== 'undefined' && input instanceof URL) {
    return input.toString();
  }
  return (input as Request).url;
}

let installed = false;
let handled = false;
let goToLogin: () => void = () => window.location.assign('/login');

/** Tandai sesi berakhir, buang token, lalu arahkan ke halaman login. Hanya sekali. */
export function expireSession(): void {
  if (handled) {
    return;
  }
  handled = true;

  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {
    // sessionStorage diblokir: login tetap jalan, hanya tanpa penjelasan.
  }
  clearAuth();

  // Cookie sesi gambar ikut dibersihkan (sudah tidak sah, tetapi tidak perlu tersisa).
  try {
    fetch(`${API_CONFIG.baseURL}/auth/logout`, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
    }).catch(() => undefined);
  } catch {
    // Tidak kritis.
  }

  if (window.location.pathname !== '/login') {
    goToLogin();
  }
}

/** Membungkus window.fetch satu kali. Aman dipanggil berulang. */
export function installSessionGuard(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  installed = true;

  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await original(input, init);
    if (response.status === 401) {
      const facts: ResponseFacts = {
        url: readUrl(input),
        status: response.status,
        authorization: readAuthorization(input, init),
        storedToken: getToken(),
      };
      if (isExpiredSession(facts)) {
        expireSession();
      }
    }
    return response;
  };
}

/** Untuk tes: kembalikan keadaan modul dan ganti cara berpindah halaman (jsdom tidak bisa menavigasi). */
export function resetSessionGuardForTests(redirect?: () => void): void {
  installed = false;
  handled = false;
  goToLogin = redirect ?? (() => window.location.assign('/login'));
}
